import type { FC } from 'react'
import styled from '@emotion/styled'

import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'
import ButtonText from '@segment/matcha/components/ButtonText'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import ReliabilityImage from './ReliabilityImage'

const Description = () => {
  const pageConfig = usePageConfig()
  return (
    <>
      We’ve built our services to be highly available and reliable, so that our customers have access to the features
      they need, at any time. Our platform is designed to deliver{` `}
      <a href={getAbsoluteUrl(`/infrastructure/#api`, pageConfig)}>99.9999%</a>
      {` `}
      uptime while providing transparent updates on any issues we're investigating.
    </>
  )
}

const copy = {
  overline: `Reliability`,
  heading: `Robust platform reliability measures`,
  description: <Description />,
}

const link = {
  text: `Learn about Segment Reliability`,
  url: `/infrastructure/`,
}

const Reliability: FC = () => {
  const pageConfig = usePageConfig()
  return (
    <Wrapper id="infrastructure">
      <StyledSectionBase variant="2Col" copyVariant="narrow">
        <SectionCopy>
          <CopyTextBase>
            <CopyText.Overline>{copy.overline}</CopyText.Overline>
            <CopyText.Heading variant="h2" tag="h3">
              {copy.heading}
            </CopyText.Heading>
            <CopyText.Description variant="pLarge">{copy.description}</CopyText.Description>
            <LinkWrapper size="medium" tag="a" href={getAbsoluteUrl(link.url, pageConfig)} iconEnd={ChevronRightIcon}>
              {link.text}
            </LinkWrapper>
          </CopyTextBase>
        </SectionCopy>

        <Section.Contents>
          <ReliabilityImage />
        </Section.Contents>
      </StyledSectionBase>
    </Wrapper>
  )
}

const Wrapper = styled.section`
  display: grid;
  ${({ theme }) => theme.breakpoints.down(`md`)} {
    padding-top: 45px;
  }
`

const StyledSectionBase = styled(SectionBase)`
  overflow: visible;
  ${Section.Wrapper} {
    ${({ theme }) => theme.breakpoints.down(`md`)} {
      padding-top: 0;
      grid-template-areas:
        'contents'
        'copy';
    }
  }
`

const LinkWrapper = styled(ButtonText)`
  padding-top: 0;
`

const SectionCopy = styled(Section.Copy)`
  display: flex;
  align-items: center;

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    max-width: 440px;
    justify-self: end;
  }
`

export default Reliability
